import { LuCalendarDays } from 'react-icons/lu';
import { GoPerson } from 'react-icons/go';
import { HiOutlineShare } from 'react-icons/hi';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import DATE_FORMATS from '@constants/dateFormat';
import { IJobStats } from '@th-types/jobs.type';
import IJobDetail, { JobMember } from '@th-types/job.detail.type';
import { formatLocalDate, UTC_TIMEZONE } from '@utils/DateUtils';
import { BASE_PATH } from '@utils/Environment';
import { useUserContext } from '@state/userContext';
import TwoPersonIcon from '@components/Icons/TwoPerson/TwoPerson';
import NotificationBellIcon from '@components/Icons/NotificationBell/NotificationBell';
import ViewOffSlashIcon from '@components/Icons/ViewOffSlash/ViewOffSlash';
import ClipboardIcon from '@components/Icons/Clipboard/Clipboard';
import DiamondIcon from '@components/Icons/Diamond/Diamond';
import {
  Badge,
  Menu,
  ActionIcon,
  Paper,
  Grid,
  Stack,
  Group,
  Text,
  Box,
  Anchor,
} from '@mantine/core';
import { SlOptionsVertical } from 'react-icons/sl';
import { PiPulseBold } from 'react-icons/pi';
import {
  emptyJobStats,
  getAddressLink,
  getFilledPosition,
  getTotalApplicants,
  getTotalHiddenApplicants,
  isJobHidden,
  LARGE_ICONS_SIZE,
  SMALL_ICONS_SIZE,
} from '@utils/JobUtils';
import { CandidateStage } from '@company/types/job-overview.type';
import SquareIndicator from './SquareIndicator';

import './styles.css';

interface Props {
  job: IJobDetail;
  jobStats?: IJobStats;
  hideJob: (job: IJobDetail) => void;
  showJobMembers: (members: JobMember[]) => void;
  handleOpenModalShareJobLink: (job: IJobDetail) => void;
}

export default function JobTableItemDesktop({
  job,
  jobStats,
  hideJob,
  showJobMembers,
  handleOpenModalShareJobLink,
}: Props) {
  const { id: companyMemberId } = useUserContext();

  const {
    id,
    position,
    eventName,
    brandRepresented,
    localizedStart,
    localizedEnd,
    datePublished,
    address,
    members,
    nationalJob,
  } = job;

  const stats = jobStats ?? emptyJobStats(id);

  // Deep link into the job detail candidates panel with the matching tab selected
  const jobDetailTabLink = (stage: CandidateStage) =>
    `${BASE_PATH}jobs/${id}?tab=${stage}`;

  return (
    <Paper p={10} fz={14}>
      <Grid align="center" gutter={0}>
        <Grid.Col span="auto" style={{ alignSelf: 'stretch' }}>
          <Stack gap={4} h="100%">
            <Anchor
              component={Link}
              to={`${BASE_PATH}jobs/${id}`}
              target="_blank"
              fw={700}
              fz={16}
              lh={1}
              tt="capitalize"
              c="inherit"
              underline="never"
            >
              {eventName}
              {!datePublished && (
                <Badge variant="draft" tt="none" ml={8}>
                  Draft
                </Badge>
              )}
            </Anchor>
            <Text fz={13}>
              {position} at{' '}
              <Text span fw={700} fz={14}>
                {brandRepresented}
              </Text>
            </Text>

            <Box fz={13}>{getAddressLink(nationalJob, address)}</Box>

            <Group gap="md" wrap="nowrap" mt="auto">
              <Group gap={4} wrap="nowrap">
                <LuCalendarDays size={SMALL_ICONS_SIZE} />
                <Text span fz={14}>
                  {formatLocalDate(
                    localizedStart,
                    DATE_FORMATS.DATE_FORMAT_SHORT,
                    UTC_TIMEZONE
                  )}{' '}
                  -{' '}
                  {localizedEnd
                    ? formatLocalDate(
                        localizedEnd,
                        DATE_FORMATS.DATE_FORMAT_SHORT,
                        UTC_TIMEZONE
                      )
                    : 'on going'}
                </Text>
              </Group>
              <Group
                gap={4}
                wrap="nowrap"
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  showJobMembers(members.filter((item) => item.isInternal))
                }
              >
                <GoPerson size={SMALL_ICONS_SIZE} />
                <Text span fw={700} td="underline" fz={14}>
                  Internal:{' '}
                  {members?.filter((item) => item.isInternal).length || 0}
                </Text>
              </Group>
            </Group>
          </Stack>
        </Grid.Col>

        {/* square indicators */}
        <Grid.Col span="content">
          <Group gap={10} justify="flex-end" wrap="nowrap">
            <SquareIndicator
              icon={<TwoPersonIcon size={32} color="var(--silver)" />}
              label="Hired"
              value={getFilledPosition(job, stats)}
              to={jobDetailTabLink(CandidateStage.HIRED)}
            />
            <SquareIndicator
              icon={
                <NotificationBellIcon
                  size={32}
                  color="var(--silver)"
                  dotColor={
                    stats.countNewApplicants > 0 ? 'var(--yellow)' : undefined
                  }
                />
              }
              label="New Applicants"
              value={stats.countNewApplicants}
              to={jobDetailTabLink(CandidateStage.NEW)}
              testId="new-applicants-square"
            />
            <SquareIndicator
              icon={<NotificationBellIcon size={32} color="var(--silver)" />}
              label="All Applicants"
              value={getTotalApplicants(stats)}
              to={jobDetailTabLink(CandidateStage.APPLICANTS)}
            />
            <SquareIndicator
              icon={<ClipboardIcon size={32} color="var(--silver)" />}
              label="Considering"
              value={stats.countConsideredWorkers}
              to={jobDetailTabLink(CandidateStage.CONSIDERING)}
            />
            <SquareIndicator
              icon={<DiamondIcon size={32} color="var(--silver)" />}
              label="Suggested"
              value={stats.countSuggestedWorkers}
              to={jobDetailTabLink(CandidateStage.SUGGESTED)}
            />
            <SquareIndicator
              icon={<ViewOffSlashIcon size={32} color="var(--silver)" />}
              label="Hidden"
              value={getTotalHiddenApplicants(stats)}
              to={jobDetailTabLink(CandidateStage.HIDDEN)}
            />
          </Group>
        </Grid.Col>

        <Grid.Col span="content" ml="10px" style={{ alignSelf: 'flex-start' }}>
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="transparent" data-testid="job-actions-menu">
                <SlOptionsVertical color="var(--black)" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<HiOutlineShare size={LARGE_ICONS_SIZE} />}
                onClick={() => handleOpenModalShareJobLink(job)}
                data-testid="share-job-action"
              >
                Share Job
              </Menu.Item>
              {isJobHidden(job, companyMemberId) ? (
                <Menu.Item
                  leftSection={<FiEye size={LARGE_ICONS_SIZE} />}
                  onClick={() => hideJob(job)}
                  data-testid="unhide-job-action"
                >
                  Unhide Job From My List
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={<FiEyeOff size={LARGE_ICONS_SIZE} />}
                  onClick={() => hideJob(job)}
                  data-testid="hide-job-action"
                >
                  Hide Job From My List
                </Menu.Item>
              )}
              <Menu.Item
                leftSection={<PiPulseBold size={LARGE_ICONS_SIZE} />}
                component={Link}
                to={`${BASE_PATH}jobs/${id}?activeTab=activityTracker`}
                target="_blank"
                data-testid="activity-tracker-action"
              >
                Activity Tracker
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Grid.Col>
      </Grid>
    </Paper>
  );
}
